import {
  Box,
  Button,
  Center,
  Divider,
  Grid,
  GridProps,
  Loader,
  PasswordInput,
  Stack,
  Text,
  TextInput,
  Title,
  useMantineTheme,
} from "@advarra-engineering/advarra-design-system-react";

import {
  adsColorSlate400,
  adsSpacingM,
  adsSpacingS,
  adsColorSlate100,
} from "@advarra-engineering/advarra-design-system-design-tokens";
import * as React from "react";
import { ChangeEvent, useEffect } from "react";
import { useTranslation } from "react-i18next";
import { PasswordRequirementChecker } from "./PasswordRequirementChecker";
import validatePassword from "../logic/validatePassword";
import fetchPasswordRequirements from "../rest/fetchPasswordRequirements";
import { PasswordRecommendations } from "./PasswordRecommendations";
import createAccount from "../rest/createAccount";
import { Message, ValidTokenState } from "../state/State";
import { IconInfoCircle } from "@tabler/icons-react";
import { ErrorCodes } from "../@types/ErrorCodes";
import { ParseKeys as IntlKey, TFunction } from "i18next";
import ScrollToBottomBox from "./ScrollToBottomBox";
import { useState } from "react";

interface CreateAccountProps {
  state: ValidTokenState;
  dispatch: React.Dispatch<Message>;
}
export function CreateAccount(props: CreateAccountProps) {
  const { state, dispatch } = props;
  const { t } = useTranslation();
  const theme = useMantineTheme();
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    const token = state.tokenValid.data.invitationToken;

    fetchPasswordRequirements(token)
      .then((pr) => {
        dispatch({ action: "set-password-requirements", value: pr });
        setLoading(false);
      })
      .catch(() => {
        dispatch({ action: "undefined-error" });
        setLoading(false);
      });
  }, [dispatch, state.tokenValid.data.invitationToken]);

  const meetsPasswordRequirements = validatePassword({
    requirements: state.passwordRequirements,
    password: state.createAccount.password.value || "",
    confirmPassword: state.createAccount.passwordConfirmation.value || "",
  });

  const requirePassword = !state.tokenValid.data.isFederated;
  // If any of the following are true, we need to disable the button.
  const disableCreateButton = [
    // Is the form being submitted?
    state.submitting,
    // Are there any password requirements that aren't met?
    requirePassword &&
      Object.values(meetsPasswordRequirements).some((m) => m === false),
    // Are there any fields that are still set to empty string (i.e. don't have
    // a value?)
    Object.entries(state.createAccount)
      .filter(
        (a) =>
          requirePassword ||
          (a[0] !== "password" && a[0] !== "passwordConfirmation"),
      )
      .map((a) => a[1].value)
      // Check if any field is null before checking if its an empty string to prevent a Null Pointer exception
      .some((f: string) => f === null || f.trim() === ""),
    // Has the user scrolled to the bottom of the TnCs?
    state.hasReadTnC === false,
  ].some((e) => e);

  return (
    <form
      onSubmit={(e) => e.preventDefault()}
      data-testid="create-account-form"
    >
      <Stack spacing="2rem">
        <Grid align="stretch" justify="stretch">
          <Grid.Col>
            <Title
              c="turquoise"
              order={1}
              data-testid="create-account-page-title"
            >
              {t("create-account.page-title")}
            </Title>
          </Grid.Col>
          <Grid.Col>
            <Divider />
          </Grid.Col>
          <Grid.Col>
            <Text
              size="sm"
              italic
              sx={{
                display: "flex",
                alignItems: "center",
                gap: "5px",
                lineHeight: "initial",
              }}
              data-testid="all-fields-are-required"
            >
              <IconInfoCircle size={theme.fontSizes.md} />
              {t("create-account.all-fields-are-required")}
            </Text>
          </Grid.Col>
          <Grid.Col xs={12} sm={6}>
            <TextInput
              data-testid="first-name"
              label={t("create-account.first-name")}
              placeholder={t("create-account.placeholder-first-name")}
              // Prevent the React warning if firstName is null,
              // as it is an optional field in invitation messages and can become null if not provided.
              // React gives a warning with null values
              value={state.createAccount.firstName.value || ""}
              error={errorTranslation(t, state.createAccount.firstName.error)}
              onChange={(e: ChangeEvent<HTMLInputElement>) =>
                dispatch({ action: "set-first-name", value: e.target.value })
              }
              onBlur={(e: ChangeEvent<HTMLInputElement>) =>
                dispatch({
                  action: "set-first-name",
                  value: e.target.value.trim(),
                })
              }
            />
          </Grid.Col>
          <Grid.Col xs={12} sm={6}>
            <TextInput
              data-testid="last-name"
              label={t("create-account.last-name")}
              placeholder={t("create-account.placeholder-last-name")}
              // Prevent the React warning if lastName is null,
              // as it is an optional field in invitation messages and can become null if not provided.
              // React gives a warning with null values
              value={state.createAccount.lastName.value || ""}
              error={errorTranslation(t, state.createAccount.lastName.error)}
              onChange={(e: ChangeEvent<HTMLInputElement>) =>
                dispatch({ action: "set-last-name", value: e.target.value })
              }
              onBlur={(e: ChangeEvent<HTMLInputElement>) =>
                dispatch({
                  action: "set-last-name",
                  value: e.target.value.trim(),
                })
              }
            />
          </Grid.Col>
          <Grid.Col>
            <TextInput
              data-testid="email"
              label={t("create-account.email-confirm")}
              placeholder={t("create-account.placeholder-email-confirm")}
              value={state.createAccount.email.value}
              autoComplete="off"
              error={errorTranslation(t, state.createAccount.email.error)}
              onChange={(e: ChangeEvent<HTMLInputElement>) =>
                dispatch({ action: "set-email", value: e.target.value })
              }
              onBlur={(e: ChangeEvent<HTMLInputElement>) =>
                dispatch({ action: "set-email", value: e.target.value.trim() })
              }
            />
          </Grid.Col>
        </Grid>
        <Grid align="stretch" justify="stretch">
          {loading ? (
            <Loader />
          ) : (
            <>
              {!state.tokenValid.data.isFederated && (
                <>
                  <Grid.Col>
                    <Title
                      c="turquoise"
                      order={2}
                      data-testid="create-a-password"
                    >
                      {t("create-account.create-a-password")}
                    </Title>
                  </Grid.Col>
                  <Grid.Col>
                    <PasswordRecommendations />
                  </Grid.Col>
                  <Grid.Col xs={12} sm={6}>
                    <Grid>
                      <Grid.Col>
                        <PasswordInput
                          data-testid="password"
                          label={t("create-account.password")}
                          placeholder={t("create-account.placeholder-password")}
                          maxLength={state.passwordRequirements.maxLength}
                          autoComplete="new-password"
                          value={state.createAccount.password.value}
                          error={errorTranslation(
                            t,
                            state.createAccount.password.error,
                          )}
                          onChange={(e: ChangeEvent<HTMLInputElement>) =>
                            dispatch({
                              action: "set-password",
                              value: e.target.value,
                            })
                          }
                        />
                      </Grid.Col>
                      <Grid.Col>
                        <PasswordInput
                          data-testid="confirm-password"
                          label={t("create-account.password-confirm")}
                          placeholder={t(
                            "create-account.placeholder-password-confirm",
                          )}
                          maxLength={state.passwordRequirements.maxLength}
                          autoComplete="new-password"
                          value={state.createAccount.passwordConfirmation.value}
                          error={errorTranslation(
                            t,
                            state.createAccount.passwordConfirmation.error,
                          )}
                          onChange={(e: ChangeEvent<HTMLInputElement>) =>
                            dispatch({
                              action: "set-password-confirmation",
                              value: e.target.value,
                            })
                          }
                        />
                      </Grid.Col>
                    </Grid>
                  </Grid.Col>
                </>
              )}
              {requirePassword && (
                <Box
                  style={{
                    padding: "0 20px",
                    flex: 1,
                  }}
                >
                  <div
                    style={{ display: "flex", justifyContent: "space-between" }}
                  >
                    <div style={{ flex: 1, overflow: "auto" }}>
                      <PasswordRequirementChecker
                        requirements={state.passwordRequirements}
                        checks={meetsPasswordRequirements}
                      />
                    </div>
                  </div>
                </Box>
              )}
            </>
          )}
          <Grid.Col>
            <BorderCard>
              <Grid.Col
                style={{
                  borderBottom: "1px solid" + `${adsColorSlate100}`,
                  padding: 0,
                  marginBottom: "8px",
                }}
              >
                <ScrollToBottomBox
                  sx={{ maxHeight: "13em", overflowY: "auto" }}
                  onScrollToBottom={() =>
                    dispatch({ action: "read-terms-and-conditions" })
                  }
                  hasScrolled={state.hasReadTnC === true}
                >
                  <Text
                    fz="md"
                    lh="lg"
                    dangerouslySetInnerHTML={{
                      __html: state.tokenValid.data.termsAndConditionsContents,
                    }}
                  ></Text>
                </ScrollToBottomBox>
              </Grid.Col>

              <Grid.Col xs={12} sm={6} sx={{ alignItems: "center" }}>
                <Center>
                  <Box fz="sm">{t("terms-and-conditions.instructions")}</Box>
                </Center>
              </Grid.Col>
              <Grid.Col xs={12} sm={6}>
                <Button
                  onClick={() => onSubmit(state, dispatch)}
                  type="submit"
                  fullWidth
                  disabled={disableCreateButton}
                  data-testid="create-account"
                  leftIcon={state.submitting ? <Loader size="sm" /> : <></>}
                  size="lg"
                >
                  {t(
                    state.submitting
                      ? "create-account.submitting"
                      : "create-account.create-account",
                  )}
                </Button>
              </Grid.Col>
            </BorderCard>
          </Grid.Col>
          <Grid.Col></Grid.Col>
        </Grid>
      </Stack>
    </form>
  );
}

const BorderCard = (props: GridProps) => {
  return (
    <Grid
      mt="md"
      sx={{
        margin: "0",
        border: `solid 1px ${adsColorSlate400}`,
        borderRadius: adsSpacingS,
        padding: adsSpacingM,
      }}
      {...props}
    ></Grid>
  );
};

const onSubmit = (
  state: ValidTokenState,
  dispatch: React.Dispatch<Message>,
) => {
  dispatch({ action: "complete-registration" });
  createAccount({
    firstName: state.createAccount.firstName.value.trim(),
    lastName: state.createAccount.lastName.value.trim(),
    email: state.createAccount.email.value.trim(),
    // We do NOT want to trim the passwords.
    password: state.createAccount.password.value,
    passwordConfirmation: state.createAccount.passwordConfirmation.value,
    isFederated: state.tokenValid.data.isFederated,
    invitationToken: state.tokenValid.data.invitationToken,
    termsAndConditionsVersion: state.tokenValid.data.termsAndConditionsVersion,
  })
    .then((resp) => {
      if (resp.redirect !== undefined) {
        window.location.replace(resp.redirect);
        return;
      }
      // Handle known error codes
      handleCompleteRegistrationErrors(dispatch, resp.code as ErrorCodes);
    })
    .catch((e) => console.error(e));
};

function handleCompleteRegistrationErrors(
  dispatch: React.Dispatch<Message>,
  code: ErrorCodes,
) {
  switch (code) {
    case ErrorCodes.E0001:
      dispatch({ action: "unmet-password-requirements" });
      return;
    case ErrorCodes.E0002:
      dispatch({ action: "undefined-error" });
      break;
    case ErrorCodes.E0003:
      dispatch({ action: "notify-wrong-email" });
      break;
    case ErrorCodes.E0004:
      dispatch({ action: "wrong-email-exceeded-allowed" });
      break;
    case ErrorCodes.E0005:
      dispatch({ action: "password-compromised" });
      break;
    case ErrorCodes.E0006:
      dispatch({ action: "username-used" });
      break;
    case ErrorCodes.E0007:
      dispatch({ action: "invalid-email-format" });
      break;
    case ErrorCodes.E0101:
      dispatch({ action: "link-not-valid" });
      break;
    case ErrorCodes.E0102:
      dispatch({ action: "invitation-expired" });
      break;
    default:
      dispatch({ action: "undefined-error" });
      return;
  }
}

function errorTranslation(t: TFunction, key?: IntlKey) {
  if (key === undefined) {
    return undefined;
  }
  return t(key);
}
